import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ResourceCard from '@/components/base/ResourceCard';
import Pagination from '@/components/base/Pagination';
import FilterPanel from '@/components/feature/FilterPanel';
import { getCategoryMeta, resources } from '@/mocks/resources';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 6;

type SortKey = 'latest' | 'hot';

export default function Category() {
  const { categoryName = '' } = useParams();
  const meta = getCategoryMeta(categoryName);

  usePageMeta(
    meta ? `${meta.name} - 国语对白做受69影视资源` : '分类不存在 - 国语对白做受69',
    meta
      ? `${meta.intro} 涵盖${meta.name}类国语对白影音资源，支持地区、年份、类型多维筛选与排序浏览。`
      : '你访问的分类不存在，请返回首页浏览其他国语对白影音资源。',
    meta
      ? `${meta.name},国语对白做受69,${meta.name}资源,国语影音,${meta.desc}`
      : '国语对白做受69,影音资源'
  );

  const [regionSelected, setRegionSelected] = useState<string[]>([]);
  const [yearSelected, setYearSelected] = useState<number[]>([]);
  const [genreSelected, setGenreSelected] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>('latest');
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const toggleValue = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

  const toggleRegion = (value: string) => {
    setRegionSelected((prev) => toggleValue(prev, value));
    setPage(1);
  };
  const toggleYear = (value: number) => {
    setYearSelected((prev) => toggleValue(prev, value));
    setPage(1);
  };
  const toggleGenre = (value: string) => {
    setGenreSelected((prev) => toggleValue(prev, value));
    setPage(1);
  };
  const resetFilters = () => {
    setRegionSelected([]);
    setYearSelected([]);
    setGenreSelected([]);
    setPage(1);
  };

  const filtered = useMemo(() => {
    let list = resources.filter((item) => item.category === categoryName);
    if (regionSelected.length) {
      list = list.filter((item) => regionSelected.includes(item.region));
    }
    if (yearSelected.length) {
      list = list.filter((item) => yearSelected.includes(item.year));
    }
    if (genreSelected.length) {
      list = list.filter((item) =>
        item.genres.some((genre) => genreSelected.includes(genre))
      );
    }
    return [...list].sort((a, b) =>
      sort === 'hot'
        ? b.views - a.views
        : a.publishDate < b.publishDate
          ? 1
          : -1
    );
  }, [categoryName, regionSelected, yearSelected, genreSelected, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handlePageChange = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!meta) {
    return (
      <div className="flex min-h-screen flex-col bg-background-100">
        <Navbar />
        <main className="mx-auto flex w-full max-w-[1360px] flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-500">
            <i className="ri-emotion-sad-line" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            未找到该分类
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            你访问的分类不存在，去首页看看其他精彩内容吧。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex h-10 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-label text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-100">
      <Navbar />
      <main className="mx-auto w-full max-w-[1360px] flex-1 px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '分类' },
            { label: meta.name },
          ]}
        />

        <div className="mt-5 mb-6 flex items-start gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
            <i className={`${meta.icon} text-3xl`} />
          </span>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {meta.name}
            </h1>
            <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-foreground-600">
              {meta.intro}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
          <aside>
            <button
              type="button"
              onClick={() => setFilterOpen((v) => !v)}
              className="mb-3 flex w-full cursor-pointer items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm font-label text-foreground-900 lg:hidden"
            >
              <span className="flex items-center gap-2">
                <i className="ri-filter-3-line text-primary-600" />
                筛选条件
              </span>
              <i
                className={`ri-arrow-down-s-line text-lg transition-transform ${
                  filterOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            <div
              className={`${filterOpen ? 'block' : 'hidden'} lg:sticky lg:top-24 lg:block`}
            >
              <FilterPanel
                regionSelected={regionSelected}
                yearSelected={yearSelected}
                genreSelected={genreSelected}
                onToggleRegion={toggleRegion}
                onToggleYear={toggleYear}
                onToggleGenre={toggleGenre}
                onReset={resetFilters}
              />
            </div>
          </aside>

          <div>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-600">
                共 <span className="font-semibold text-primary-600">{filtered.length}</span> 个资源
                {totalPages > 1 && ` · 第 ${page}/${totalPages} 页`}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500">排序：</span>
                <div className="flex overflow-hidden rounded-md border border-background-300">
                  <button
                    type="button"
                    onClick={() => {
                      setSort('latest');
                      setPage(1);
                    }}
                    className={`cursor-pointer px-3 py-1.5 text-xs font-label transition-colors ${
                      sort === 'latest'
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    按更新时间
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSort('hot');
                      setPage(1);
                    }}
                    className={`cursor-pointer px-3 py-1.5 text-xs font-label transition-colors ${
                      sort === 'hot'
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    按热度
                  </button>
                </div>
              </div>
            </div>

            {pageItems.length > 0 ? (
              <>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                  {pageItems.map((item) => (
                    <ResourceCard key={item.id} resource={item} />
                  ))}
                </div>
                <div className="mt-10">
                  <Pagination
                    current={page}
                    total={totalPages}
                    onChange={handlePageChange}
                  />
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-20 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-500">
                  <i className="ri-search-eye-line" />
                </span>
                <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">
                  没有找到符合条件的资源
                </h3>
                <p className="mt-2 max-w-sm text-sm text-foreground-600">
                  试试减少筛选条件，或重置筛选查看该分类下的全部内容。
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-6 inline-flex h-10 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-label text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-refresh-line" />
                  重置筛选
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}